// Страница управления упражнениями для преподавателей: две вкладки — список упражнений и управление разделами.

import { useEffect, useState } from 'react';
import { Container, Button, Spinner, Row, Col, Card, Badge, Form, Modal } from 'react-bootstrap';
import {
  ArrowLeft,
  FileTextFill,
  ChevronRight,
  CalendarFill,
  Grid3x2GapFill,
  ListUl,
  Trash,
  PencilSquare,
} from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import useExerciseStore from '../../store/exerciseStore';
import AppNavbar from '../../components/common/AppNavbar';
import ExerciseWizard from '../../components/manages/ExerciseWizard';
import { deleteExercise } from '../../api/exerciseApi';

// ── Константы ──────────────────────────────────────────────────────────────────

const LEVEL_VARIANT = {
  1: 'success',
  2: 'info',
  3: 'primary',
  4: 'warning',
  5: 'danger',
};

function variantWord(n) {
  if (n === 1) return 'вариант';
  if (n >= 2 && n <= 4) return 'варианта';
  return 'вариантов';
}

// ── Exercise Card (Grid) ───────────────────────────────────────────────────────

function ExerciseCard({ exercise, onClick, onDelete, onEdit, levelLookup }) {
  const levelLabel = levelLookup[exercise.complexity_level_id] ?? `Ур. ${exercise.complexity_level_id}`;
  const levelVariant = LEVEL_VARIANT[exercise.complexity_level_id] ?? 'secondary';
  const formattedDate = exercise.created_date ? exercise.created_date.slice(0, 10) : null;
  const variantsCount = exercise.exercise_variants?.length ?? null;

  return (
    <Card
      onClick={onClick}
      style={{
        background: '#ffffff',
        border: '1px solid #e8eaf0',
        borderRadius: 'var(--ewl-radius-card)',
        boxShadow: 'var(--ewl-shadow-sm)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        position: 'relative',
        height: '100%',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = 'var(--ewl-shadow-md)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'var(--ewl-shadow-sm)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Card.Body className="p-4">
        <div
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            color: '#adb5bd',
          }}
        >
          <ChevronRight size={16} />
        </div>

        <div className="mb-3">
          <FileTextFill size={28} color="#4f6ef7" />
        </div>

        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap" style={{ paddingRight: '1.5rem' }}>
          <span className="fw-bold" style={{ fontSize: '0.9375rem', color: '#1e2a4a' }}>
            {exercise.name}
          </span>
          <Badge bg={levelVariant} pill style={{ fontSize: '0.7rem', fontWeight: 600 }}>
            {levelLabel}
          </Badge>
        </div>

        {exercise.task && (
          <div
            className="text-muted mb-2"
            style={{
              fontSize: '0.8rem',
              lineHeight: '1.5',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            <span style={{ fontWeight: 600, color: '#6c757d' }}>Задание: </span>
            {exercise.task}
          </div>
        )}

        {exercise.description && (
          <div
            className="text-muted mb-3"
            style={{
              fontSize: '0.8125rem',
              lineHeight: '1.5',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {exercise.description}
          </div>
        )}

        <div
          className="d-flex justify-content-between align-items-center"
          style={{ marginTop: 'auto', fontSize: '0.8125rem', color: '#6c757d' }}
        >
          <span>
            {variantsCount !== null
              ? `${variantsCount} ${variantWord(variantsCount)}`
              : '— вариантов'}
          </span>
          <div className="d-flex align-items-center gap-2">
            {formattedDate && (
              <span className="d-flex align-items-center gap-1">
                <CalendarFill size={12} />
                {formattedDate}
              </span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              title="Редактировать упражнение"
              style={{
                border: 'none', background: 'transparent', padding: '0.1rem 0.25rem',
                cursor: 'pointer', color: '#adb5bd', lineHeight: 1,
                borderRadius: '0.25rem', transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#0d6efd'}
              onMouseLeave={e => e.currentTarget.style.color = '#adb5bd'}
            >
              <PencilSquare size={13} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              title="Удалить упражнение"
              style={{
                border: 'none', background: 'transparent', padding: '0.1rem 0.25rem',
                cursor: 'pointer', color: '#adb5bd', lineHeight: 1,
                borderRadius: '0.25rem', transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#dc3545'}
              onMouseLeave={e => e.currentTarget.style.color = '#adb5bd'}
            >
              <Trash size={13} />
            </button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

// ── Exercise List Item (List) ──────────────────────────────────────────────────

function ExerciseListItem({ exercise, onClick, onDelete, onEdit, levelLookup }) {
  const levelLabel = levelLookup[exercise.complexity_level_id] ?? `Ур. ${exercise.complexity_level_id}`;
  const levelVariant = LEVEL_VARIANT[exercise.complexity_level_id] ?? 'secondary';
  const variantsCount = exercise.exercise_variants?.length ?? 0;
  const formattedDate = exercise.created_date?.slice(0, 10) ?? null;

  return (
    <Card
      className="border-0"
      style={{
        background: '#fff',
        border: '1px solid #e8eaf0',
        borderRadius: '0.625rem',
        boxShadow: 'var(--ewl-shadow-sm)',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
      }}
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = 'var(--ewl-shadow-md)')}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'var(--ewl-shadow-sm)')}
    >
      <Card.Body className="py-2 px-3">
        <div className="d-flex align-items-center gap-3">
          <FileTextFill size={20} color="#4f6ef7" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="fw-semibold" style={{ fontSize: '0.9rem', color: '#1e2a4a' }}>
              {exercise.name}
            </span>
          </div>
          <Badge bg={levelVariant} pill style={{ fontSize: '0.7rem', flexShrink: 0 }}>
            {levelLabel}
          </Badge>
          <span className="text-muted" style={{ fontSize: '0.8rem', flexShrink: 0 }}>
            {variantsCount} {variantWord(variantsCount)}
          </span>
          {formattedDate && (
            <span
              className="text-muted d-flex align-items-center gap-1"
              style={{ fontSize: '0.8rem', flexShrink: 0 }}
            >
              <CalendarFill size={11} />
              {formattedDate}
            </span>
          )}
          <ChevronRight size={14} color="#adb5bd" style={{ flexShrink: 0 }} />
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            title="Редактировать упражнение"
            style={{
              border: 'none', background: 'transparent', padding: '0.1rem 0.25rem',
              cursor: 'pointer', color: '#adb5bd', lineHeight: 1,
              borderRadius: '0.25rem', transition: 'color 0.15s', flexShrink: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#0d6efd'}
            onMouseLeave={e => e.currentTarget.style.color = '#adb5bd'}
          >
            <PencilSquare size={13} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Удалить упражнение"
            style={{
              border: 'none', background: 'transparent', padding: '0.1rem 0.25rem',
              cursor: 'pointer', color: '#adb5bd', lineHeight: 1,
              borderRadius: '0.25rem', transition: 'color 0.15s', flexShrink: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#dc3545'}
            onMouseLeave={e => e.currentTarget.style.color = '#adb5bd'}
          >
            <Trash size={13} />
          </button>
        </div>
      </Card.Body>
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function ExercisesManagePage() {
  const navigate = useNavigate();
  const {
    types, exercises, fetchTypes, fetchExercises,
    teacherSelectedTypeId, setTeacherSelectedTypeId,
    complexityLevels, fetchComplexityLevels,
    updateExercise,
  } = useExerciseStore();

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [wizardShow, setWizardShow] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // { id, name, complexity_level_id }
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    Promise.all([fetchTypes(), fetchExercises(), fetchComplexityLevels()])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Установить первый тип по умолчанию если сохранённый не найден или не задан
  useEffect(() => {
    if (types.length === 0) return;
    const savedValid = teacherSelectedTypeId !== null && types.some((t) => t.id === teacherSelectedTypeId);
    if (!savedValid) {
      setTeacherSelectedTypeId(types[0].id);
    }
  }, [types]);

  // Сброс фильтра уровня при смене типа
  useEffect(() => {
    setSelectedLevel(null);
  }, [teacherSelectedTypeId]);

  const byType = teacherSelectedTypeId !== null
    ? exercises.filter((ex) => ex.type_id === teacherSelectedTypeId)
    : [];

  // Lookup: { id -> name } из данных БД
  const levelLookup = Object.fromEntries(complexityLevels.map((l) => [l.id, l.name]));

  const filteredExercises = selectedLevel !== null
    ? byType.filter((ex) => ex.complexity_level_id === selectedLevel)
    : byType;

  const currentType = types.find((t) => t.id === teacherSelectedTypeId);

  const handleWizardHide = () => {
    setWizardShow(false);
    fetchExercises();
  };

  const handleEditSave = async () => {
    if (!editTarget || !editTarget.name.trim()) return;
    setEditSaving(true);
    try {
      const currentExercise = exercises.find((ex) => ex.id === editTarget.id);
      await updateExercise(editTarget.id, {
        name: editTarget.name.trim(),
        complexity_level_id: editTarget.complexity_level_id,
        type_id: currentExercise?.type_id ?? null,
        description: currentExercise?.description ?? null,
        task: currentExercise?.task ?? null,
      });
      await fetchExercises();
      setEditTarget(null);
    } catch {
      // можно показать ошибку — пока тихо
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteExercise(deleteTarget.id);
      await fetchExercises();
    } catch {
      // игнорируем — упражнение могло уже быть удалено
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">

          {/* ── Section header ── */}
          <div
            className="d-flex justify-content-between align-items-start mb-4 pb-3"
            style={{ borderBottom: '1px solid #e9ecef' }}
          >
            <div className="d-flex align-items-start gap-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate('/teacher/exercise-types')}
                style={{ borderRadius: '0.5rem', flexShrink: 0, marginTop: '0.15rem' }}
              >
                <ArrowLeft size={16} />
              </Button>
              <div>
                <h5 className="fw-bold mb-0" style={{ color: '#1e2a4a' }}>
                  {currentType ? currentType.name : 'Упражнения'}
                </h5>
                <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
                  Управление упражнениями данного типа
                </div>
              </div>
            </div>
            <Button
              variant="dark"
              onClick={() => setWizardShow(true)}
              style={{ borderRadius: '0.5rem', flexShrink: 0 }}
            >
              + Создать упражнение
            </Button>
          </div>

          {/* ── Selector + view toggle ── */}
          <div className="d-flex align-items-center gap-3 mb-3">
            <Form.Select
              size="sm"
              style={{ maxWidth: 260 }}
              value={teacherSelectedTypeId ?? ''}
              onChange={(e) => setTeacherSelectedTypeId(Number(e.target.value))}
            >
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Form.Select>
            <div className="ms-auto d-flex gap-1">
              <Button
                size="sm"
                variant={viewMode === 'grid' ? 'dark' : 'outline-secondary'}
                onClick={() => setViewMode('grid')}
              >
                <Grid3x2GapFill size={15} />
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'list' ? 'dark' : 'outline-secondary'}
                onClick={() => setViewMode('list')}
              >
                <ListUl size={15} />
              </Button>
            </div>
          </div>

          {/* ── Level stats + filter ── */}
          {!loading && byType.length > 0 && (
            <div className="d-flex align-items-center gap-2 flex-wrap mb-4">
              <button
                onClick={() => setSelectedLevel(null)}
                style={{
                  border: 'none',
                  borderRadius: '2rem',
                  padding: '0.3rem 0.85rem',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: selectedLevel === null ? '#1e2a4a' : '#f0f1f5',
                  color: selectedLevel === null ? '#fff' : '#6c757d',
                  transition: 'all 0.15s',
                }}
              >
                Все · {byType.length}
              </button>
              {complexityLevels.map((lvl) => {
                const count = byType.filter((ex) => ex.complexity_level_id === lvl.id).length;
                if (count === 0) return null;
                const isActive = selectedLevel === lvl.id;
                const bgMap = { 1: '#198754', 2: '#0dcaf0', 3: '#0d6efd', 4: '#ffc107', 5: '#dc3545' };
                const colorMap = { 1: '#fff', 2: '#000', 3: '#fff', 4: '#000', 5: '#fff' };
                return (
                  <button
                    key={lvl.id}
                    onClick={() => setSelectedLevel(isActive ? null : lvl.id)}
                    style={{
                      border: 'none',
                      borderRadius: '2rem',
                      padding: '0.3rem 0.85rem',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: isActive ? (bgMap[lvl.id] ?? '#1e2a4a') : '#f0f1f5',
                      color: isActive ? (colorMap[lvl.id] ?? '#fff') : '#6c757d',
                      transition: 'all 0.15s',
                    }}
                  >
                    {lvl.name} · {count}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Content ── */}
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredExercises.length === 0 ? (
            <Card
              className="text-center py-5"
              style={{
                background: '#fff',
                border: '1px solid #e8eaf0',
                borderRadius: 'var(--ewl-radius-card)',
                boxShadow: 'var(--ewl-shadow-sm)',
              }}
            >
              <Card.Body>
                <FileTextFill size={40} color="#dee2e6" className="mb-3" />
                <h6 className="fw-semibold text-muted mb-1">Упражнений пока нет</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                  Создайте первое упражнение для этого типа
                </p>
                <Button variant="dark" size="sm" onClick={() => setWizardShow(true)}>
                  + Создать упражнение
                </Button>
              </Card.Body>
            </Card>
          ) : viewMode === 'grid' ? (
            <Row xs={1} md={2} className="g-3">
              {filteredExercises.map((exercise) => (
                <Col key={exercise.id}>
                  <ExerciseCard
                    exercise={exercise}
                    levelLookup={levelLookup}
                    onClick={() =>
                      navigate(
                        `/teacher/exercise-types/${teacherSelectedTypeId}/exercises/edit`,
                        { state: { exerciseId: exercise.id } }
                      )
                    }
                    onDelete={() => setDeleteTarget({ id: exercise.id, name: exercise.name })}
                    onEdit={() => setEditTarget({ id: exercise.id, name: exercise.name, complexity_level_id: exercise.complexity_level_id })}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="d-flex flex-column gap-2">
              {filteredExercises.map((exercise) => (
                <ExerciseListItem
                  key={exercise.id}
                  exercise={exercise}
                  levelLookup={levelLookup}
                  onClick={() =>
                    navigate(
                      `/teacher/exercise-types/${teacherSelectedTypeId}/exercises/edit`,
                      { state: { exerciseId: exercise.id } }
                    )
                  }
                  onDelete={() => setDeleteTarget({ id: exercise.id, name: exercise.name })}
                  onEdit={() => setEditTarget({ id: exercise.id, name: exercise.name, complexity_level_id: exercise.complexity_level_id })}
                />
              ))}
            </div>
          )}
        </Container>
      </div>

      {/* ── Exercise Wizard ── */}
      <ExerciseWizard show={wizardShow} onHide={handleWizardHide} />

      {/* ── Edit Exercise Modal ── */}
      <Modal show={editTarget !== null} onHide={() => !editSaving && setEditTarget(null)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>Редактировать упражнение</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem 1.5rem' }}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Название <span className="text-danger">*</span></Form.Label>
            <Form.Control
              value={editTarget?.name ?? ''}
              onChange={(e) => setEditTarget((t) => ({ ...t, name: e.target.value }))}
              placeholder="Название упражнения"
              disabled={editSaving}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Уровень сложности</Form.Label>
            <Form.Select
              value={editTarget?.complexity_level_id ?? 1}
              onChange={(e) => setEditTarget((t) => ({ ...t, complexity_level_id: Number(e.target.value) }))}
              disabled={editSaving}
            >
              {complexityLevels.map((lvl) => (
                <option key={lvl.id} value={lvl.id}>{lvl.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
          <Button variant="outline-secondary" size="sm" onClick={() => setEditTarget(null)} disabled={editSaving}
            style={{ borderRadius: '0.5rem', fontWeight: 500 }}>
            Отмена
          </Button>
          <Button variant="dark" size="sm" onClick={handleEditSave}
            disabled={editSaving || !editTarget?.name?.trim()}
            style={{ borderRadius: '0.5rem', fontWeight: 600 }}>
            {editSaving ? <><Spinner animation="border" size="sm" className="me-1" />Сохраняем...</> : 'Сохранить'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Confirm Modal ── */}
      <Modal show={deleteTarget !== null} onHide={() => !deleting && setDeleteTarget(null)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>Удалить упражнение?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem 1.5rem' }}>
          <p className="mb-1 fw-semibold" style={{ color: '#1e2a4a', fontSize: '0.9rem' }}>
            {deleteTarget?.name}
          </p>
          <p className="mb-0 text-muted" style={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
            Упражнение будет удалено без возможности восстановления. Продолжить?
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
          <Button variant="outline-secondary" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}
            style={{ borderRadius: '0.5rem', fontWeight: 500 }}>
            Отмена
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteConfirm} disabled={deleting}
            style={{ borderRadius: '0.5rem', fontWeight: 600 }}>
            {deleting ? <><Spinner animation="border" size="sm" className="me-1" />Удаляем...</> : 'Удалить'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
